import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';

const ProfileSection = () => {
  const { user, userProfile, loading: authLoading, updateProfile } = useAuth();
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    phone: "",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: ""
    }
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load user data from database
  useEffect(() => {
    if (userProfile) {
      // Parse full_name to get first and last name
      const fullName = userProfile.full_name || '';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setProfileData({
        firstName,
        lastName,
        email: userProfile.email || '',
        company: userProfile.company_name || '',
        phone: userProfile.phone || '',
        address: userProfile.address || {
          street: "",
          city: "",
          state: "",
          zipCode: "",
          country: ""
        }
      });
    }
  }, [userProfile]);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (addressField, value) => {
    setProfileData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [addressField]: value
      }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Prepare data for database update
      const fullName = `${profileData.firstName} ${profileData.lastName}`.trim();
      const updateData = {
        full_name: fullName,
        email: profileData.email,
        company_name: profileData.company,
        phone: profileData.phone,
        address: profileData.address
      };

      const result = await updateProfile(updateData);
      
      if (result.success) {
        setIsEditing(false);
      } else {
        console.error('Failed to update profile:', result.error);
        // You might want to show an error message to the user here
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSaving(false);
    }
  };

  // Generate avatar initials from user's name
  const generateAvatarInitials = () => {
    const firstName = profileData.firstName || '';
    const lastName = profileData.lastName || '';
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  };

  // Generate avatar background color based on initials
  const generateAvatarColor = () => {
    const initials = generateAvatarInitials();
    
    // If initials are empty or only one character, use Octalchip primary color
    if (!initials || initials.length <= 1) {
      return '#00d68f';
    }
    
    // Use Octalchip primary color for most cases, with slight variations
    const octalchipVariations = [
      '#00d68f', // Primary Octalchip green
      '#00c47a', // Slightly darker green
      '#00e8a3', // Slightly lighter green
      '#00d68f', // Primary green again
      '#00c47a', // Darker green
      '#00e8a3', // Lighter green
      '#00d68f', // Primary green
      '#00c47a'  // Darker green
    ];
    
    const index = initials.charCodeAt(0) % octalchipVariations.length;
    return octalchipVariations[index];
  };

  return (
    <div className="space-y-6">
      {/* Loading State */}
      {authLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <Icon name="Loader" size={24} className="text-primary animate-spin" />
            <span className="text-text-secondary">Loading profile...</span>
          </div>
        </div>
      ) : !userProfile ? (
        <div className="text-center py-12">
          <Icon name="User" size={48} className="text-text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No Profile Found</h3>
          <p className="text-text-secondary">Unable to load your profile information.</p>
        </div>
      ) : (
        <>
          {/* Profile Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Profile Information</h2>
              <p className="text-sm text-text-secondary mt-1">
                Update your personal information and profile settings
              </p>
            </div>
            {!isEditing ? (
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                iconName="Edit"
                iconPosition="left"
              >
                Edit Profile
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  onClick={() => setIsEditing(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  loading={isSaving}
                  iconName="Save"
                  iconPosition="left"
                >
                  Save Changes
                </Button>
              </div>
            )}
          </div>

      {/* Avatar Section */}
      <div className="card p-6">
        <div className="flex items-center space-x-6">
          <div className="relative">
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center text-white font-semibold text-xl"
              style={{ backgroundColor: generateAvatarColor() }}
            >
              {generateAvatarInitials()}
            </div>
          </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-text-primary mb-1">Profile Avatar</h3>
              <p className="text-xs text-text-secondary">
                Your avatar will be displayed across the website and represents your professional identity.
              </p>
            </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              First Name
            </label>
            <Input
              type="text"
              value={profileData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter first name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Last Name
            </label>
            <Input
              type="text"
              value={profileData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter last name"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Email Address
            </label>
            <Input
              type="email"
              value={profileData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter email address"
            />
            <p className="text-xs text-text-secondary mt-1">
              This email will be used for account notifications and login
            </p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Phone Number
            </label>
            <Input
              type="tel"
              value={profileData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter phone number"
            />
          </div>
        </div>
      </div>

      {/* Company Information */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">Company Information</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Company Name
            </label>
            <Input
              type="text"
              value={profileData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter company name"
            />
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">Address Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Street Address
            </label>
            <Input
              type="text"
              value={profileData.address.street}
              onChange={(e) => handleAddressChange('street', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter street address"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              City
            </label>
            <Input
              type="text"
              value={profileData.address.city}
              onChange={(e) => handleAddressChange('city', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter city"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              State/Province
            </label>
            <Input
              type="text"
              value={profileData.address.state}
              onChange={(e) => handleAddressChange('state', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter state or province"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              ZIP/Postal Code
            </label>
            <Input
              type="text"
              value={profileData.address.zipCode}
              onChange={(e) => handleAddressChange('zipCode', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter ZIP or postal code"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Country
            </label>
            <Input
              type="text"
              value={profileData.address.country}
              onChange={(e) => handleAddressChange('country', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter country"
            />
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default ProfileSection;
